import { num, toggle, type Choice } from '../../themes/settingFields';
import { named } from '../../themes/settingFields';
import { element } from './definition';
import { look } from './styleTokens';

// A carousel: one child, one slide. Anything at all may be a slide — a picture, a whole column of copy
// over a picture — because the thing that makes this an element is the moving between them, not what
// moves. Sleek's hero is exactly this, and ours was one still frame of it.
//
// `slideWidth` under 100 is what makes it read as a carousel while it is standing still: the reference
// holds its hero to 69% of the window so the slides on either side show past both edges, which is the
// only thing on the page telling a shopper there is more than one.
//
// Reduced motion and keyboard focus pause autoplay. Hovering pauses it only when requested.

/**
 * How a slide's picture arrives. Taken from the reference's own motion vocabulary and its own numbers:
 * `zoom-out` is scale 1.3 settling to 1 over 1.5s, `zoom-out-sm` is 1.1 over 1s, both on the same
 * cubic-bezier(0, 0, .3, 1). Anyone who has asked for less motion is given none of it.
 */
const MOTIONS: readonly Choice[] = [
  ['zoom-out', 'Zoom out'], ['zoom-out-sm', 'Zoom out, gently'], ['none', 'Still']
];

/** What a shopper is given to move between slides themselves, since autoplay alone is not an affordance. */
const CONTROLS: readonly Choice[] = [
  ['dots', 'Dots'], ['arrows', 'Arrows'], ['both', 'Dots and arrows'], ['none', 'Nothing']
];

/** The seconds a slide is held. Fewer than two is unreadable; more than a minute is not a carousel. */
const MIN_HOLD = 2;
const MAX_HOLD = 60;

/** How much of the box one slide may take. Under a third it is a filmstrip, not a slideshow. */
const MIN_SHARE = 30;
const FULL = 100;

/** The slideshow: a box whose children are its slides, one shown at a time. */
export const slideshowElement = element('slideshow', {
  label: 'Slideshow',
  description: 'A carousel whose slides are whatever you put inside it, one shown at a time.',
  category: 'Layouts',
  icon: 'gallery-horizontal',
  props: [
    {
      ...num('slideWidth', 'Slide width', MIN_SHARE, FULL),
      help: 'A share of the width, so under 100 the slides either side show through. Measured on a desktop; a phone always shows one whole slide.'
    },
    toggle('autoplay', 'Move on its own'),
    toggle('pauseOnHover', 'Pause when hovered'),
    { ...num('holdSeconds', 'Seconds on each slide', MIN_HOLD, MAX_HOLD), help: 'Ignored for anyone who has asked for less motion.' },
    named('motion', 'How a slide arrives', MOTIONS),
    named('controls', 'What a shopper gets', CONTROLS),
    toggle('loop', 'Return to the first after the last')
  ],
  defaults: {
    props: { slideWidth: FULL, motion: 'zoom-out', autoplay: true, pauseOnHover: false, holdSeconds: 5, controls: 'dots', loop: true },
    styles: look({ display: 'block', position: 'relative', width: '100%', overflow: 'hidden' })
  },
});
